import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import { selectAuthUser } from '../../../../redux/auth/auth.selectors';
import { selectCurrentWorkspace, selectWorkspaceMembers, selectWorkspaceInvites } from '../../../../redux/workspace/workspace.selectors';
import { workspaceFetchMembersRequest, workspaceFetchInvitesRequest } from '../../../../redux/workspace/workspace.actions';
import { toggleInvitePeople, toggleModalMemberRemove, toggleModalWorkspaceLeave, toggleModalInviteRevoke } from '../../../../redux/main/main.actions';
import { showNotify } from '../../../../redux/notify/notify.actions';
import { getAvatarUrl } from '../../../../utils/get-avatar-url';
import { copyToClipboard } from '../../../../utils/copy-to-clipboard';

import './SettingsMembers.scss';

const formatRole = (role) => (role ? role.charAt(0).toUpperCase() + role.slice(1) : '—');

const formatDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const SettingsMembers = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useSelector(selectAuthUser);
    const workspace = useSelector(selectCurrentWorkspace);
    const members = useSelector(selectWorkspaceMembers) || [];
    const invites = useSelector(selectWorkspaceInvites) || [];

    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    const myRole = workspace?.my_role;
    const canManage = myRole === 'owner' || myRole === 'admin';

    useEffect(() => {
        if (workspace?.id) dispatch(workspaceFetchMembersRequest(workspace.id));
    }, [dispatch, workspace?.id]);

    // Pending invites are owner/admin-only (the endpoint 403/404s otherwise),
    // so only fetch them when the viewer can manage members.
    useEffect(() => {
        if (workspace?.id && canManage) dispatch(workspaceFetchInvitesRequest(workspace.id));
    }, [dispatch, workspace?.id, canManage]);

    // Close the row options menu on outside click / Escape.
    useEffect(() => {
        if (!openMenuId) return undefined;
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpenMenuId(null); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [openMenuId]);

    const handleRemove = (member) => {
        setOpenMenuId(null);
        dispatch(toggleModalMemberRemove(member));
    };

    // Revoke a pending invite — open a confirmation modal (mirrors Remove member).
    // The actual revoke dispatch lives in ModalInviteRevoke's confirm handler.
    const handleRevokeInvite = (invite) => {
        setOpenMenuId(null);
        dispatch(toggleModalInviteRevoke(invite));
    };

    const handleLeave = () => {
        setOpenMenuId(null);
        dispatch(toggleModalWorkspaceLeave(workspace));
    };

    const handleCopyEmail = async (e, email) => {
        e?.preventDefault?.();
        if (!email) return;
        const ok = await copyToClipboard(email);
        if (ok) {
            dispatch(showNotify({ message: 'email_copied' }));
        }
    };

    // Build the row menu for the current viewer. Blocked actions are shown
    // disabled with an explanatory tooltip rather than hidden. The kebab shows
    // whenever a row has at least one applicable item.
    const buildMenuItems = (member) => {
        const isSelf = member.user_id === currentUser?.id;
        const isOwnerRow = member.role === 'owner';
        const items = [];

        if (isSelf) {
            // Leave on your own row — owners must transfer ownership first.
            if (isOwnerRow) {
                items.push({ key: 'leave', label: t('settings_page.members.leave_workspace_item'), disabled: true, tooltip: t('settings_page.profile.leave_owner_tooltip') });
            } else {
                items.push({ key: 'leave', label: t('settings_page.members.leave_workspace_item'), onClick: handleLeave });
            }
        } else {
            // Remove member — shown on every other row, disabled when not allowed.
            if (!canManage) {
                // items.push({ key: 'remove', label: 'Remove member...', disabled: true, tooltip: 'Only owners and admins can remove members' });
                items.push({ key: 'remove', label: t('settings_page.members.remove_member_item'), disabled: true, tooltip: t('settings_page.members.remove_owner_only_tooltip') });
            } else if (isOwnerRow) {
                items.push({ key: 'remove', label: t('settings_page.members.remove_member_item'), disabled: true, tooltip: t('settings_page.members.remove_owner_row_tooltip') });
            } else {
                items.push({ key: 'remove', label: t('settings_page.members.remove_member_item'), onClick: () => handleRemove(member) });
            }
        }

        return items;
    };

    return (
        <div className='settings-wrap settings-members-wrap'>
            <div className='settings-members-head'>
                <div className='settings-header'>{t('settings_page.members.header')}</div>
                <button className='settings-row-btn settings-invite-btn' onClick={() => dispatch(toggleInvitePeople())}>
                    {t('b.invite')}
                </button>
            </div>

            <div className='settings-section settings-section-members'>
                <div className='members-table'>
                    <div className='members-row members-head-row'>
                        <div className='members-col col-name'>{t('settings_page.members.col_name')}</div>
                        <div className='members-col col-email'>{t('settings_page.members.col_email')}</div>
                        <div className='members-col col-role'>{t('settings_page.members.col_role')}</div>
                        <div className='members-col col-date'>{t('settings_page.members.col_joined')}</div>
                        <div className='members-col col-options'></div>
                    </div>

                    <div className="members-row members-section-row">
                        <div className="members-col col-name">{t('settings_page.members.active_members')}</div>
                    </div>

                    {members.length === 0 ? (
                        <div className='members-empty'>{t('settings_page.members.no_members')}</div>
                    ) : (
                        members.map((member) => {
                            const isSelf = member.user_id === currentUser?.id;
                            const menuItems = buildMenuItems(member);
                            const displayName = member.name || member.email || '—';
                            const initial = (member.name || member.email || '?')[0].toUpperCase();

                            return (
                                <div className={`members-row ${openMenuId === member.id ? 'clicked' : ''}`} key={member.id}>
                                    <div className='members-col col-name'>
                                        <div className='member-avatar'>
                                            {member.image
                                                ? <img className='member-avatar-img' src={getAvatarUrl(member.image, 44)} width='28' height='28' referrerPolicy='no-referrer' alt='' />
                                                : <div className='member-avatar-placeholder'>{initial}</div>}
                                        </div>
                                        <span className='member-name'>
                                            {displayName}
                                            {isSelf ? <span className='member-you'>{t('m_diagram_share.you')}</span> : null}
                                        </span>
                                    </div>
                                    <div className={`members-col col-email ${!member.email ? 'col-email-empty' : ''}`}>
                                        {member.email
                                            ? <span className='member-email' onClick={(e) => handleCopyEmail(e, member.email)}>{member.email}</span>
                                            : '—'}
                                    </div>
                                    <div className='members-col col-role'>
                                        <span className={`member-role-badge role-${member.role || 'member'}`}>{formatRole(member.role)}</span>
                                    </div>
                                    <div className='members-col col-date'>{formatDate(member.joined_at || member.created_at)}</div>
                                    <div className='members-col col-options'>
                                        {menuItems.length > 0 && (
                                            <div className='member-options' ref={openMenuId === member.id ? menuRef : null}>
                                                <button
                                                    className={`member-options-btn ${openMenuId === member.id ? 'clicked' : ''}`}
                                                    onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                                                    // aria-label={t('settings_page.members.options_aria')}
                                                >
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor'><circle cx='12' cy='5' r='2' /><circle cx='12' cy='12' r='2' /><circle cx='12' cy='19' r='2' /></svg>
                                                </button>
                                                {openMenuId === member.id && (
                                                    <div className='member-options-menu'>
                                                        {menuItems.map((item) => (
                                                            item.disabled ? (
                                                                <Tooltip.Root key={item.key} ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                                                                    <Tooltip.Trigger asChild>
                                                                        <button className='member-options-item disabled' type='button' aria-disabled='true'>
                                                                            {item.label}
                                                                        </button>
                                                                    </Tooltip.Trigger>
                                                                    <Tooltip.Portal>
                                                                        <Tooltip.Content className='tooltip-content tooltip-controls' side='bottom' align='center' sideOffset={12}>
                                                                            <span>{item.tooltip}</span>
                                                                        </Tooltip.Content>
                                                                    </Tooltip.Portal>
                                                                </Tooltip.Root>
                                                            ) : (
                                                                <button key={item.key} className='member-options-item' onClick={item.onClick}>
                                                                    {item.label}
                                                                </button>
                                                            )
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {invites.length > 0 && (
                        <>
                            <div className="members-row members-section-row">
                                <div className="members-col col-name">{t('settings_page.members.invited_section')}</div>
                            </div>

                            {invites.map((invite) => {
                                const menuId = `invite-${invite.id}`;
                                const initial = (invite.email || '?')[0].toUpperCase();

                                return (
                                    <div className={`members-row ${openMenuId === menuId ? 'clicked' : ''}`} key={menuId}>
                                        <div className='members-col col-name'>
                                            <div className='member-avatar'>
                                                <div className='member-avatar-placeholder'>{initial}</div>
                                            </div>
                                            {/* Invites have no name — the email is the identifier (shown
                                                once here, copyable); the Email column stays empty. */}
                                            <span className='member-name member-email' onClick={(e) => handleCopyEmail(e, invite.email)}>{invite.email || '—'}</span>
                                        </div>
                                        <div className='members-col col-email col-email-empty'>—</div>
                                        <div className='members-col col-role'>
                                            <span className={`member-role-badge role-${invite.role || 'member'}`}>{t('settings_page.members.role_invited_suffix', { role: formatRole(invite.role) })}</span>
                                        </div>
                                        <div className='members-col col-date col-date-empty'>—</div>
                                        <div className='members-col col-options'>
                                            <div className='member-options' ref={openMenuId === menuId ? menuRef : null}>
                                                <button
                                                    className={`member-options-btn ${openMenuId === menuId ? 'clicked' : ''}`}
                                                    onClick={() => setOpenMenuId(openMenuId === menuId ? null : menuId)}
                                                    aria-label={t('settings_page.members.options_aria')}
                                                >
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor'><circle cx='12' cy='5' r='2' /><circle cx='12' cy='12' r='2' /><circle cx='12' cy='19' r='2' /></svg>
                                                </button>
                                                {openMenuId === menuId && (
                                                    <div className='member-options-menu'>
                                                        <button className='member-options-item' onClick={() => handleRevokeInvite(invite)}>
                                                            {t('settings_page.members.remove_invite_item')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

export default SettingsMembers;
